import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { DataTable } from "@/components/DataTable";
import { usePartsAndLocations } from "@/hooks/usePartsAndLocations";
import { apiFetch } from "@/utils/api";

interface BomLine {
  id: string;
  part_name: string;
  part_number: string;
  quantity_required: number;
}

interface SystemBomEditorProps {
  systemId: string;
}

export function SystemBomEditor({ systemId }: SystemBomEditorProps) {
  const { parts, loading: partsLoading } = usePartsAndLocations();
  const [lines, setLines] = useState<BomLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ partId: "", quantityRequired: 1 });

  const loadLines = useCallback(async () => {
    const data = await apiFetch<{ lines: BomLine[] }>(
      `/admin/bom?systemId=${encodeURIComponent(systemId)}`,
    );
    setLines(data.lines);
  }, [systemId]);

  useEffect(() => {
    setLoading(true);
    setError("");
    loadLines()
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load BOM"))
      .finally(() => setLoading(false));
  }, [loadLines]);

  const addLine = async () => {
    if (!form.partId) return;
    setSaving(true);
    setError("");
    try {
      await apiFetch("/admin/bom", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          systemId,
          partId: form.partId,
          quantityRequired: form.quantityRequired,
        }),
      });
      setForm({ partId: "", quantityRequired: 1 });
      await loadLines();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add BOM line");
    } finally {
      setSaving(false);
    }
  };

  const deleteLine = async (id: string) => {
    if (!window.confirm("Remove this part from the BOM?")) return;
    setSaving(true);
    setError("");
    try {
      await apiFetch(`/admin/bom/${id}`, { method: "DELETE" });
      await loadLines();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to remove BOM line");
    } finally {
      setSaving(false);
    }
  };

  if (loading || partsLoading) {
    return <p className="text-sm text-muted-foreground">Loading BOM…</p>;
  }

  return (
    <div className="space-y-4 pt-4 border-t">
      <p className="text-sm font-semibold text-[#1e3a4f]">Bill of Materials</p>

      <div className="grid gap-3 sm:grid-cols-4 items-end">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor={`bom-part-${systemId}`}>Part</Label>
          <Select
            id={`bom-part-${systemId}`}
            value={form.partId}
            onChange={(e) => setForm({ ...form, partId: e.target.value })}
          >
            <option value="">Select part…</option>
            {parts.map((p) => (
              <option key={p.id} value={p.id}>
                {p.part_name}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`bom-qty-${systemId}`}>Qty required</Label>
          <Input
            id={`bom-qty-${systemId}`}
            type="number"
            min={1}
            value={form.quantityRequired}
            onChange={(e) => setForm({ ...form, quantityRequired: Number(e.target.value) || 1 })}
          />
        </div>
        <Button
          type="button"
          disabled={saving || !form.partId}
          onClick={() => void addLine()}
          className="bg-[#1e3a4f] hover:bg-[#163041]"
        >
          Add to BOM
        </Button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <DataTable
        columns={[
          { key: "part", header: "Part", render: (l) => l.part_name },
          { key: "num", header: "Part #", render: (l) => l.part_number || "—" },
          {
            key: "qty",
            header: "Required",
            className: "text-right",
            render: (l) => String(l.quantity_required),
          },
          {
            key: "action",
            header: "",
            render: (l) => (
              <Button
                size="sm"
                variant="secondary"
                disabled={saving}
                onClick={() => void deleteLine(l.id)}
              >
                Remove
              </Button>
            ),
          },
        ]}
        rows={lines}
        rowKey={(l) => l.id}
        emptyMessage="No BOM lines for this system."
      />
    </div>
  );
}
